/*
 * Profile-wizard org configuration — canon §5.2 genderPolicy + §5.3 eventFields.
 *
 * Both axes are org-configured per event (canon §10 variant matrix). The DC
 * exposes them as props (genderPolicy default `binary`; eventFields default
 * `Trumin defaults (phone optional, no address)`) — NEVER hardcoded in the
 * form itself.
 *
 * // TODO(collision-D): reconcile canon eventFields presets with settings
 * // participantConfig. The shipped settings-owned contract
 * // (src/data/participantConfig.ts DataCollectionResolved: per-field
 * // {key, collect} booleans, no address field, no genderPolicy, no
 * // optional-vs-required distinction) cannot express the canon eventFields
 * // model below. Escalated to boardroom (collision D). Until settled, the
 * // wizard is built to the CANON structure behind this seam, and an
 * // unresolved/unspecified config resolves to the canon defaults
 * // (genderPolicy binary; Trumin defaults — phone optional, no address).
 */
import type { DataCollectionResolved, ParticipantFieldKey } from '../../data/participantConfig'

/** Canon §5.2: org gender policy. */
export type GenderPolicy = 'binary' | 'flagged' | 'third'

/** false = never rendered (no "(optional)" filler — canon §11.4). */
export type FieldMode = false | 'optional' | 'required'

export interface WizardEventFields {
  address: FieldMode
  phone: FieldMode
}

export interface WizardConfig {
  genderPolicy: GenderPolicy
  eventFields: WizardEventFields
  /** Event-level question toggles (CMT-2026-08-29-022 / Duncan ruling
   *  BOARDROOM-2026-08-29-110613): false ⇒ the T-shirt / language question
   *  NEVER renders — no greyed-out step, no gap in the progress indicators —
   *  and it is never required for completeness. Both default TRUE (asked);
   *  an event config's absent key ⇒ ask (fail-toward-asking). */
  askLanguage: boolean
  askShirt: boolean
  /** Organiser display name for policy notices/helpboxes — [demo] `Apex
   *  Games` in the DC; production comes with the event's org record. */
  organiser?: string
}

/** Canon §5.3 eventFields presets — names verbatim from the DC prop enum. */
export const EVENT_FIELDS_PRESETS: Record<string, WizardEventFields> = {
  'Trumin defaults (phone optional, no address)': { address: false, phone: 'optional' },
  'Address required by organiser': { address: 'required', phone: 'optional' },
  'Address optional': { address: 'optional', phone: 'optional' },
  'Phone required': { address: false, phone: 'required' },
  'Minimum — no address or phone': { address: false, phone: false },
}

export const DEFAULT_EVENT_FIELDS: WizardEventFields =
  EVENT_FIELDS_PRESETS['Trumin defaults (phone optional, no address)']

export const DEFAULT_WIZARD_CONFIG: WizardConfig = {
  genderPolicy: 'binary',
  eventFields: DEFAULT_EVENT_FIELDS,
  askLanguage: true,
  askShirt: true,
}

/** Whether an event's resolved data_collection asks for `key`: explicit
 *  `collect:false` ⇒ no; present+collect ⇒ yes; ABSENT ⇒ yes — fail-toward-
 *  asking, per CMT-2026-08-29-022 (the established questionnaire-gate
 *  posture: a missing signal never silently drops a question). */
const asksField = (resolved: DataCollectionResolved, key: ParticipantFieldKey): boolean => {
  const f = resolved.fields.find((x) => x.key === key)
  return f ? f.collect : true
}

/**
 * Resolve the wizard config from the settings participant-config seam.
 * LIVE for the CMT-2026-08-29-022 question toggles: `tshirt_size` / `language`
 * `{key, collect:false}` rows switch the matching wizard question OFF; an
 * absent/null resolved config keeps the ask-everything defaults.
 * See TODO(collision-D) above for the REST of the config: the resolved
 * data_collection shape still carries neither genderPolicy nor address nor
 * optional/required — those axes keep mapping to the canon defaults until the
 * contracts are reconciled.
 */
export function resolveWizardConfig(resolved: DataCollectionResolved | null): WizardConfig {
  if (!resolved) return DEFAULT_WIZARD_CONFIG
  return {
    ...DEFAULT_WIZARD_CONFIG, // genderPolicy/eventFields — see TODO(collision-D)
    askLanguage: asksField(resolved, 'language'),
    askShirt: asksField(resolved, 'tshirt_size'),
  }
}
